import React, { useState } from 'react';
import axios from 'axios';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';

export async function readApiError(error) {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return json.message || 'Request failed';
    } catch {
      return 'Request failed';
    }
  }
  return data?.message || error.message || 'Request failed';
}

export async function downloadDataStoreSheet(woocommerceProductId, eventName) {
  const response = await axios.get('/api/v1/export-data-store-sheet', {
    params: {
      woocommerce_product_id: woocommerceProductId,
      ...(eventName ? { event_name: eventName } : {}),
    },
    responseType: 'blob',
    withCredentials: true,
  });
  const match = /filename="?([^"]+)"?/.exec(response.headers['content-disposition'] || '');
  const fallback = String(eventName || `list-${woocommerceProductId}`)
    .replace(/[^\w]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80);
  const filename = match?.[1] || `${fallback || `list-${woocommerceProductId}`}.xlsx`;
  const url = URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const UploadCsv = ({ woocommerce_product_id, event_name }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      await downloadDataStoreSheet(woocommerce_product_id, event_name);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error(await readApiError(error));
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Select an Excel or CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('csv', selectedFile);
    formData.append('woocommerce_product_id', woocommerce_product_id);
    formData.append('event_name', event_name || '');

    setUploadLoading(true);
    setResult(null);
    try {
      const response = await axios.post('/api/v1/sync-data-store-sheet', formData, {
        withCredentials: true,
      });
      const data = response.data;
      setResult(data);
      setSelectedFile(null);
      toast.success(data.message || 'Sheet synced');
      if (data.skipped?.length) {
        const preview = data.skipped.slice(0, 5).map((item) => (
          `row ${item.row ?? '?'}${item.db_id ? ` (db_id ${item.db_id})` : ''}: ${item.reason}`
        )).join('; ');
        toast.warning(`${data.skipped.length} skipped. ${preview}`);
      }
    } catch (error) {
      toast.error(await readApiError(error));
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, width: 520, maxWidth: '90vw', borderRadius: '12px' }}>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, pr: 4 }}>
        {event_name || 'Upload Event Data'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Download this list, fill missing cells, and add new rows with a blank DB-ID. Filled DB-ID rows update that record. Blank cells are left unchanged.
      </Typography>

      <Button
        variant="outlined"
        startIcon={downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={handleDownload}
        disabled={downloadLoading}
        fullWidth
        sx={{ mb: 2 }}
      >
        {downloadLoading ? 'Preparing Excel...' : 'Download Excel'}
      </Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, width: '100%' }}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(event) => {
              setSelectedFile(event.target.files[0] || null);
              setResult(null);
            }}
            style={{ display: 'none' }}
            id={`upload-${woocommerce_product_id}`}
          />
          <label htmlFor={`upload-${woocommerce_product_id}`}>
            <Button
              component="span"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              {selectedFile ? 'Change file' : 'Select Excel or CSV'}
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
              {selectedFile.name}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploadLoading || !selectedFile}
          sx={{ minWidth: 120 }}
        >
          {uploadLoading ? <CircularProgress size={18} color="inherit" /> : 'Upload'}
        </Button>
      </Stack>

      {result && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            Updated {result.updated || 0}, inserted {result.inserted || 0}
            {result.skipped?.length ? `, skipped ${result.skipped.length}` : ''}.
          </Typography>
          {result.skipped?.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.main' }}>
              {result.skipped.slice(0, 8).map((item) => (
                `Row ${item.row ?? '?'}${item.db_id ? ` db_id ${item.db_id}` : ''}: ${item.reason}`
              )).join(' · ')}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default UploadCsv;
